import { useEffect, useRef } from "react";
import { useCart } from "~/hooks/useCart";
import { captureTouch, sameTouch, sendTouchpoint, type CapturedTouch } from "~/sdk/attribution";

/**
 * Montado uma vez no root. Captura a origem da visita (ver attribution.ts) e,
 * quando o orderForm existe, grava utmSource/utmMedium/utmCampaign no
 * `marketingData` dele -- o pedido fechado herda isso (marketingData do OMS).
 *
 * Regras:
 * - toque NOVO (veio nesta carga de página) sempre sobrescreve o do orderForm;
 * - toque ANTIGO (só do cookie) só preenche quando o orderForm ainda não tem
 *   utmSource -- evita apagar, por exemplo, o UTM que a recuperação de
 *   carrinho do WhatsApp gravou no orderForm;
 * - `...marketingData` preserva cupom e utmiCampaign (consultora), mesmo
 *   padrão de Coupon.tsx/VendorCoupon.tsx;
 * - uma tentativa por orderForm+toque (ref), pra nunca entrar em loop de
 *   re-render; best-effort, falha nunca quebra a loja.
 */
export function useAttributionSync(): void {
  const { cart, sendAttachment } = useCart();
  const touchRef = useRef<CapturedTouch | null>(null);
  const tentadoRef = useRef<string>("");

  const orderFormId = cart?.value?.orderFormId;
  const marketingData = cart?.value?.marketingData as
    | { utmSource?: string | null; utmMedium?: string | null; utmCampaign?: string | null }
    | undefined
    | null;

  useEffect(() => {
    try {
      // 1ª execução (mount): captura a URL/referrer da entrada. Vale p/ a
      // carga de página inteira -- navegação SPA seguinte reusa o resultado.
      touchRef.current ??= captureTouch();
      const captured = touchRef.current;
      // Toque novo vai ao CRM uma vez, já na entrada (não depende do
      // orderForm existir); zera pra não reenviar nos efeitos seguintes.
      if (captured?.touchpoint) {
        sendTouchpoint(captured.touchpoint);
        captured.touchpoint = null;
      }
      if (!captured?.touch || !orderFormId) return;
      const { touch, fresh } = captured;

      if (marketingData && sameTouch(touch, marketingData)) return;
      if (!fresh && marketingData?.utmSource) return;

      const chave = `${orderFormId}|${touch.utmSource}|${touch.utmMedium}|${touch.utmCampaign}`;
      if (tentadoRef.current === chave) return;
      tentadoRef.current = chave;

      void sendAttachment({
        attachment: "marketingData",
        body: { ...(marketingData ?? {}), ...touch },
      });
    } catch (error) {
      console.error("[attribution] falha ao gravar UTM no orderForm:", error);
    }
    // marketingData só importa pelos 3 campos; sendAttachment é estável o bastante
    // (recriado a cada render) e a trava por `chave` impede reenvio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderFormId, marketingData?.utmSource, marketingData?.utmMedium, marketingData?.utmCampaign]);
}
